/**
 * Configuration values used throughout the application.
 *
 * This module exports constants for:
 * - API endpoints (BACKEND_URL)
 * - Default image and PDF URLs
 * - Logo URLs
 * - Form options (phone prefixes, product categories, measurement units, etc.)
 * - Validation patterns (email regex)
 * - Currency symbols
 */

/**
 * API base URL (must end with `/`).
 * Comment/uncomment exactly one `BACKEND_URL` line below to switch target.
 */

// Local dev: Django on port 8001 (backend runserver default)
// export const BACKEND_URL = 'http://localhost:8001/v1/';

// Production / remote backend (e.g. materiahstock.com, Organoids EC2)
export const BACKEND_URL = 'https://materiahstock.com/v1/';

/**
 * Soft FE gate for Materiah AI assistant (lab users only in UI).
 * Server may still return 503 when AI_CHAT_ENABLED is false or OPENAI_API_KEY is missing.
 */
export const AI_CHAT_ENABLED = true;

/** Max user message length accepted by ai-chat messages endpoint. */
export const AI_CHAT_MAX_MESSAGE_LENGTH = 4000;

// Default URLs for images and pdfs when no specific image or pdf is provided
export const defaultImageUrl =
  'https://materiah1.s3.eu-central-1.amazonaws.com/products/No-Image-Placeholder.svg.png';

export const defaultPdfUrl =
  'https://materiah1.s3.eu-central-1.amazonaws.com/products/PDF-Placeholder-e1500896019213.png';

// Logo URL as provided by design team
export const largeLogo =
  'https://materiah1.s3.eu-central-1.amazonaws.com/design/new_materiah_icon-nobg.png';

/** Navbar favicon-sized brand mark (same asset as largeLogo). */
export const navbarLogo = largeLogo;

/** Materiah AI assistant FAB / entry icon (design team). */
export const materiahAiIcon =
  'https://materiah1.s3.eu-central-1.amazonaws.com/design/materiah_ai_icon.png';

export const HocLogo =
  'https://materiah1.s3.eu-central-1.amazonaws.com/design/thumbnail_image001_3ef1cea0ac.jpg';

export const HadassahResearchFundLogo =
  'https://materiah1.s3.eu-central-1.amazonaws.com/design/logo+(1).svg';

export const HadassahLogo =
  'https://materiah1.s3.eu-central-1.amazonaws.com/design/HadassahHospital2024.svg.webp';

export const MinyanLogo =
  'https://materiah1.s3.eu-central-1.amazonaws.com/design/minyan_logo.png';

// Available phone number prefix choices
export const PHONE_PREFIX_CHOICES = [
  { value: '050', label: '050' },
  { value: '051', label: '051' },
  { value: '052', label: '052' },
  { value: '053', label: '053' },
  { value: '054', label: '054' },
  { value: '055', label: '055' },
  { value: '056', label: '056' },
  { value: '058', label: '058' },
  { value: '059', label: '059' },
  { value: '02', label: '02' },
  { value: '03', label: '03' },
  { value: '04', label: '04' },
  { value: '05', label: '05' },
  { value: '08', label: '08' },
  { value: '09', label: '09' },
  { value: '071', label: '071' },
  { value: '072', label: '072' },
  { value: '073', label: '073' },
  { value: '074', label: '074' },
  { value: '076', label: '076' },
  { value: '077', label: '077' },
  { value: '079', label: '079' },
  { value: '1700', label: '1700' },
];

// Available categories for products
export const PRODUCT_CATEGORIES = [
  { value: 'Antibiotic', label: 'Antibiotic' },
  { value: 'Antibody', label: 'Antibody' },
  { value: 'Buffer', label: 'Buffer' },
  { value: 'Cells', label: 'Cells' },
  { value: 'Dye', label: 'Dye' },
  { value: 'Enzyme', label: 'Enzyme' },
  { value: 'Glassware', label: 'Glassware' },
  { value: 'Hormone', label: 'Hormone' },
  { value: 'Kit', label: 'Kit' },
  { value: 'Lab Equipment', label: 'Lab Equipment' },
  { value: 'Matrix', label: 'Matrix' },
  { value: 'Medication', label: 'Medication' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Plastics', label: 'Plastics' },
  { value: 'Powders', label: 'Powders' },
  { value: 'Protein', label: 'Protein' },
  { value: 'Reagent', label: 'Reagent' },
  { value: 'Sanitary', label: 'Sanitary' },
  { value: 'Solution', label: 'Solution' },
  { value: 'Supplement', label: 'Supplement' },
];

// Measurement units for products
export const PRODUCT_MEASUREMENT_UNITS = [
  { value: 'L', label: 'Litres, l' },
  { value: 'ML', label: 'Milliliters, ml' },
  { value: 'UL', label: 'Microliters, µl' },
  { value: 'KG', label: 'Kilograms, kg' },
  { value: 'G', label: 'Grams, g' },
  { value: 'MG', label: 'Milligrams, mg' },
  { value: 'UG', label: 'Micrograms, µg' },
  { value: 'Assays', label: 'Reactions/Tests/Assays' },
  { value: 'Package', label: 'Package' },
  { value: 'Box', label: 'Box' },
];

// Storage options for products
export const PRODUCT_STORAGE_OPTIONS = [
  { value: '+4', label: '+4' },
  { value: '-20', label: '-20' },
  { value: '-40', label: '-40' },
  { value: '-80', label: '-80' },
  { value: 'Room Temperature', label: 'Room Temperature' },
  { value: 'Other', label: 'Other' },
];

export const EXPIRY_CATEGORIES = [
  { value: 'N', label: 'No' },
  { value: 'Y', label: 'Yes' },
  {
    value: 'Y6M18M',
    label:
      'Yes, after 6 months irrelevant for some uses, for other uses relevant up to 18 months after expiry',
  },
  { value: 'Y1Y', label: 'Yes, can be used 12 months after expiry' },
  { value: 'Y6m', label: 'Yes, can be used 6 months after expiry' },
];
// Regular expression for validating email addresses
export const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;

export const CURRENCY_SYMBOLS = { NIS: '₪', USD: '$', EUR: '€' };

/** Currency options for project ad-hoc lines and products. */
export const PRODUCT_CURRENCY_OPTIONS = [
  { value: 'NIS', label: 'NIS (₪)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
];

// Project status options
export const PROJECT_STATUS_OPTIONS = [
  { value: 'PLANNING', label: 'Planning' },
  { value: 'QUOTING', label: 'Quoting' },
  { value: 'ORDERING', label: 'Ordering' },
  { value: 'RECEIVING', label: 'Receiving' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'ON_HOLD', label: 'On Hold' },
];

// Project product status options
export const PROJECT_PRODUCT_STATUS_OPTIONS = [
  { value: 'NEEDS_QUOTE', label: 'Needs Quote' },
  { value: 'QUOTED', label: 'Quoted' },
  { value: 'ORDERED', label: 'Ordered' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'RECEIVED', label: 'Received' },
];

export const PRODUCT_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'category', label: 'Category' },
  { value: 'unit_quantity', label: 'Unit Quantity' },
  { value: 'unit', label: 'Unit' },
  { value: 'price', label: 'Price' },
  { value: 'stock', label: 'Stock' },
  { value: 'manufacturer_name', label: 'Manufacturer' },
  { value: 'supplier_name', label: 'Supplier' },
  { value: 'updated_at', label: 'Recently Updated' },
];

export const ORDER_SORT_OPTIONS = [
  { value: 'order_num', label: 'Order Number' },
  { value: 'supplier_name', label: 'Supplier' },
  { value: 'funding', label: 'Funding' },
  { value: 'user', label: 'User' },
  { value: 'status', label: 'Status' },
  { value: 'created_at', label: 'Created At' },
];

export const PROJECT_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'status', label: 'Status' },
  { value: 'created_at', label: 'Created At' },
  { value: 'updated_at', label: 'Updated At' },
];

export const QUOTE_SORT_OPTIONS = [
  { value: 'quote_num', label: 'Quote Number' },
  { value: 'supplier_name', label: 'Supplier' },
  { value: 'user', label: 'User' },
  { value: 'created_at', label: 'Created At' },
];

export const SUPPLIER_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Created At' },
];

export const MANUFACTURER_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'created_at', label: 'Created At' },
];

export const SUPPLIER_CREDIT_SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'remaining_amount', label: 'Remaining Amount' },
  { value: 'initial_amount', label: 'Initial Amount' },
  { value: 'created_at', label: 'Created At' },
];
